import { useCallback, useState } from "react";
import { Link } from "react-router-dom";

import { AchievementToast } from "@/components/scenario/AchievementToast";
import { BaseballField } from "@/components/field/BaseballField";
import { ExplanationPanel } from "@/components/scenario/ExplanationPanel";
import { OptionButton } from "@/components/scenario/OptionButton";
import { ScenarioCard } from "@/components/scenario/ScenarioCard";
import { Button } from "@/components/ui/Button";
import { Card } from "@/components/ui/Card";
import { ErrorMessage } from "@/components/ui/ErrorMessage";
import { LoadingSpinner } from "@/components/ui/LoadingSpinner";
import { useDailyChallenge, useSubmitDailyChallenge } from "@/hooks/use-daily-challenge";
import type { AchievementDefinition, AnswerResult } from "@/types";
import { getErrorMessage } from "@/utils/error-message";

function getTimeUntilMidnight(): string {
  const now = new Date();
  const midnight = new Date(now);
  midnight.setHours(24, 0, 0, 0);
  const diff = midnight.getTime() - now.getTime();
  const hours = Math.floor(diff / (1000 * 60 * 60));
  const minutes = Math.floor((diff % (1000 * 60 * 60)) / (1000 * 60));
  return `${hours}h ${minutes}m`;
}

export function DailyChallengeCard() {
  const { data: challenge, isLoading, error } = useDailyChallenge();
  const submitAnswer = useSubmitDailyChallenge();
  const [selectedOptionId, setSelectedOptionId] = useState<string | null>(null);
  const [result, setResult] = useState<AnswerResult | null>(null);
  const [communityStats, setCommunityStats] = useState<{ totalAttempts: number; totalCorrect: number } | null>(null);
  const [revealed, setRevealed] = useState(false);
  const [newAchievements, setNewAchievements] = useState<AchievementDefinition[]>([]);

  const handleOptionSelect = useCallback(
    async (optionId: string) => {
      if (revealed) return;
      setSelectedOptionId(optionId);

      try {
        const response = await submitAnswer.mutateAsync({ selectedOptionId: optionId });
        setResult(response.result);
        setCommunityStats(response.communityStats);
        setRevealed(true);
        if (response.result.newAchievements?.length > 0) {
          setNewAchievements(response.result.newAchievements);
        }
      } catch {
        setSelectedOptionId(null);
      }
    },
    [revealed, submitAnswer],
  );

  function getOptionState(
    optionId: string,
  ): "default" | "selected" | "correct" | "incorrect" {
    if (!revealed) {
      return optionId === selectedOptionId ? "selected" : "default";
    }
    if (!result) return "default";
    if (optionId === result.correctOptionId) return "correct";
    if (optionId === selectedOptionId) return "incorrect";
    return "default";
  }

  if (isLoading) return <LoadingSpinner message="Loading daily challenge..." />;
  if (error) return null;
  if (!challenge) return null;

  if (challenge.alreadyAnswered && !revealed) {
    return (
      <Card className="border-2 border-amber-200 bg-amber-50/50">
        <div className="flex items-center justify-between">
          <div>
            <p className="text-sm font-bold text-amber-700">Daily Challenge</p>
            <p className="text-xs text-amber-600">Completed! Next challenge in {getTimeUntilMidnight()}</p>
          </div>
          <span className="text-2xl">{"\u2705"}</span>
        </div>
      </Card>
    );
  }

  const communityPercent = communityStats && communityStats.totalAttempts > 0
    ? Math.round((communityStats.totalCorrect / communityStats.totalAttempts) * 100)
    : null;

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <span className="text-xl">{"\u{1F31F}"}</span>
          <h2 className="text-lg font-bold text-amber-700">Daily Challenge</h2>
        </div>
        <span className="text-xs text-gray-400">Resets in {getTimeUntilMidnight()}</span>
      </div>

      <div className="overflow-hidden rounded-xl shadow-sm">
        <BaseballField fieldState={challenge.fieldState} className="block w-full" />
      </div>

      <ScenarioCard scenario={challenge}>
        {challenge.options.map((option) => (
          <OptionButton
            key={option.id}
            label={option.text}
            state={getOptionState(option.id)}
            disabled={revealed || submitAnswer.isPending}
            onClick={() => handleOptionSelect(option.id)}
          />
        ))}
      </ScenarioCard>

      {submitAnswer.error && <ErrorMessage message={getErrorMessage(submitAnswer.error)} />}

      {revealed && result && (
        <>
          <ExplanationPanel
            correct={result.correct}
            explanation={result.explanation}
            streak={result.streak}
            xpEarned={result.xpEarned}
            onNext={() => {}}
          />
          {communityPercent !== null && (
            <Card className="text-center">
              <p className="text-sm text-gray-600">
                <span className="font-bold text-navy-700">{communityPercent}%</span> of players got this right
              </p>
            </Card>
          )}
          <Link to="/play" className="block">
            <Button className="w-full" size="lg">Keep Playing</Button>
          </Link>
        </>
      )}

      {newAchievements.length > 0 && <AchievementToast achievements={newAchievements} />}
    </div>
  );
}
